/** Site bildirimleri — admin panelinden yönetilir */

export const NOTIFICATION_TYPES = [
  { id: 'popup', label: 'Minik pencere', desc: 'Ortada veya köşede açılan popup' },
  { id: 'strip', label: 'Üst şerit', desc: 'Sayfanın üstünde sabit bant' },
  { id: 'toast', label: 'Toast', desc: 'Sağ altta otomatik kaybolan kart' },
  { id: 'banner', label: 'Banner', desc: 'Üst bant altı geniş kart' }
];

export const NOTIFICATION_VARIANTS = [
  { id: 'info', label: 'Bilgi', color: '#3b82f6' },
  { id: 'success', label: 'Başarı', color: '#10b981' },
  { id: 'warning', label: 'Uyarı', color: '#f59e0b' },
  { id: 'promo', label: 'Kampanya', color: '#a855f7' },
  { id: 'error', label: 'Önemli', color: '#ef4444' }
];

export const POPUP_POSITIONS = [
  { id: 'center', label: 'Orta (klasik pencere)' },
  { id: 'bottom-right', label: 'Sağ alt köşe' },
  { id: 'bottom-left', label: 'Sol alt köşe' },
  { id: 'top-right', label: 'Sağ üst köşe' }
];

export const POPUP_SIZES = [
  { id: 'sm', label: 'Küçük (320px)' },
  { id: 'md', label: 'Orta (400px)' },
  { id: 'lg', label: 'Geniş (480px)' }
];

export const PAGE_OPTIONS = [
  { id: 'all', label: 'Tüm sayfalar' },
  { id: '/', label: 'Ana Sayfa' },
  { id: '/paketler', label: 'Paketler' },
  { id: '/bayilik', label: 'Bayilik' },
  { id: '/kurulum', label: 'Kurulum' },
  { id: '/hakkimizda', label: 'Hakkımızda' },
  { id: '/sss', label: 'SSS' }
];

export const DEFAULT_NOTIFICATION_SETTINGS = {
  globalEnabled: true,
  maxStripVisible: 2,
  maxToastVisible: 3,
  maxPopupVisible: 1,
  toastDurationMs: 8000,
  popupAutoCloseMs: 0,
  popupBackdropBlur: true,
  respectDismissals: true
};

export const DEFAULT_NOTIFICATIONS_DOC = {
  globalEnabled: true,
  settings: { ...DEFAULT_NOTIFICATION_SETTINGS },
  items: []
};

const DISMISS_PREFIX = 'tonrex_notify_dismiss_';
const SESSION_PREFIX = 'tonrex_notify_session_';

function normalizePopupFields(item, settings) {
  const posIds = POPUP_POSITIONS.map((p) => p.id);
  const sizeIds = POPUP_SIZES.map((s) => s.id);
  return {
    popupPosition: posIds.includes(item.popupPosition) ? item.popupPosition : 'center',
    popupSize: sizeIds.includes(item.popupSize) ? item.popupSize : 'md',
    showBackdrop: item.showBackdrop !== false,
    backdropClose: item.backdropClose !== false,
    lockScroll: item.lockScroll !== false,
    openDelayMs: Math.max(0, Number(item.openDelayMs) || 0),
    autoCloseMs:
      item.autoCloseMs !== undefined && item.autoCloseMs !== null
        ? Math.max(0, Number(item.autoCloseMs) || 0)
        : Math.max(0, Number(settings.popupAutoCloseMs) || 0),
    showOncePerSession: Boolean(item.showOncePerSession),
    iconEmoji: String(item.iconEmoji || '').trim(),
    secondaryLinkText: String(item.secondaryLinkText || '').trim(),
    secondaryLinkUrl: String(item.secondaryLinkUrl || '').trim()
  };
}

export function sanitizeNotificationsDoc(raw) {
  const base = {
    globalEnabled: true,
    settings: { ...DEFAULT_NOTIFICATION_SETTINGS },
    items: []
  };
  if (!raw || typeof raw !== 'object') return base;

  const settings = { ...base.settings, ...(raw.settings || {}) };
  if (typeof raw.globalEnabled === 'boolean') {
    base.globalEnabled = raw.globalEnabled;
  } else if (typeof settings.globalEnabled === 'boolean') {
    base.globalEnabled = settings.globalEnabled;
  }

  const typeIds = NOTIFICATION_TYPES.map((t) => t.id);
  const variantIds = NOTIFICATION_VARIANTS.map((v) => v.id);

  const items = Array.isArray(raw.items)
    ? raw.items
        .filter((item) => item && typeof item === 'object')
        .map((item, index) => {
          const type = typeIds.includes(item.type) ? item.type : 'popup';
          const core = {
            id: String(item.id || `n-${index}`),
            enabled: item.enabled !== false,
            type,
            variant: variantIds.includes(item.variant) ? item.variant : 'promo',
            title: String(item.title || '').trim(),
            message: String(item.message || '').trim(),
            linkText: String(item.linkText || '').trim(),
            linkUrl: String(item.linkUrl || '').trim(),
            dismissible: item.dismissible !== false,
            showOnPages:
              Array.isArray(item.showOnPages) && item.showOnPages.length
                ? item.showOnPages.map(String)
                : ['all'],
            startsAt: item.startsAt || null,
            endsAt: item.endsAt || null,
            priority: Number.isFinite(Number(item.priority)) ? Number(item.priority) : 0
          };
          return { ...core, ...normalizePopupFields(item, settings) };
        })
        .filter((item) => item.title || item.message)
    : [];

  return {
    globalEnabled: base.globalEnabled,
    settings,
    items,
    updatedAt: raw.updatedAt
  };
}

function isInSchedule(item, now = new Date()) {
  if (item.startsAt) {
    const start = new Date(item.startsAt);
    if (!Number.isNaN(start.getTime()) && now < start) return false;
  }
  if (item.endsAt) {
    const end = new Date(item.endsAt);
    if (!Number.isNaN(end.getTime()) && now > end) return false;
  }
  return true;
}

function matchesPage(item, pathname) {
  const pages = item.showOnPages || ['all'];
  if (pages.includes('all')) return true;
  return pages.some((p) => p === pathname || (p !== '/' && pathname.startsWith(p)));
}

export function isNotificationDismissed(id, { sessionOnly = false } = {}) {
  if (typeof window === 'undefined') return false;
  try {
    if (sessionOnly) {
      return sessionStorage.getItem(`${SESSION_PREFIX}${id}`) === '1';
    }
    return localStorage.getItem(`${DISMISS_PREFIX}${id}`) === '1';
  } catch {
    return false;
  }
}

export function dismissNotification(id, { sessionOnly = false } = {}) {
  if (typeof window === 'undefined') return;
  try {
    if (sessionOnly) {
      sessionStorage.setItem(`${SESSION_PREFIX}${id}`, '1');
    } else {
      localStorage.setItem(`${DISMISS_PREFIX}${id}`, '1');
    }
  } catch {
    /* ignore */
  }
}

export function clearNotificationDismissals() {
  if (typeof window === 'undefined') return;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(DISMISS_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(SESSION_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

function isItemDismissed(item, respectDismissals) {
  if (!respectDismissals) return false;
  if (item.showOncePerSession) {
    return isNotificationDismissed(item.id, { sessionOnly: true });
  }
  return isNotificationDismissed(item.id);
}

/**
 * Aktif bildirimleri döndürür (tür bazlı limit uygulanır).
 */
export function getActiveNotifications(doc, pathname, options = {}) {
  const { respectDismissals = true } = options;
  const data = sanitizeNotificationsDoc(doc);
  if (!data.globalEnabled) {
    return { strip: [], toast: [], banner: [], popup: [], settings: data.settings };
  }

  const now = new Date();
  let items = data.items.filter(
    (item) => item.enabled && isInSchedule(item, now) && matchesPage(item, pathname)
  );

  if (respectDismissals && data.settings.respectDismissals !== false) {
    items = items.filter((item) => !isItemDismissed(item));
  }

  items.sort((a, b) => (b.priority || 0) - (a.priority || 0));

  const byType = (type, max) => items.filter((i) => i.type === type).slice(0, max);

  return {
    popup: byType('popup', data.settings.maxPopupVisible ?? 1),
    strip: byType('strip', data.settings.maxStripVisible || 2),
    toast: byType('toast', data.settings.maxToastVisible || 3),
    banner: byType('banner', 5),
    settings: data.settings
  };
}

export function newNotificationItem(partial = {}) {
  return {
    id:
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `n-${Date.now()}`,
    enabled: true,
    type: 'popup',
    variant: 'promo',
    title: '',
    message: '',
    linkText: '',
    linkUrl: '',
    dismissible: true,
    showOnPages: ['all'],
    startsAt: null,
    endsAt: null,
    priority: 50,
    popupPosition: 'center',
    popupSize: 'md',
    showBackdrop: true,
    backdropClose: true,
    lockScroll: true,
    openDelayMs: 800,
    autoCloseMs: 0,
    showOncePerSession: false,
    iconEmoji: '✦',
    secondaryLinkText: '',
    secondaryLinkUrl: '',
    ...partial
  };
}
